<template>
  <section
    class="home-analytics"
    :class="{ 'home-analytics--embedded': hideTitle }"
    :aria-labelledby="hideTitle ? undefined : 'home-analytics-title'"
  >
    <h2
      v-if="!hideTitle"
      id="home-analytics-title"
      class="home-analytics__title"
    >
      {{ t('routes.analytics.list') }}
    </h2>

    <div class="home-analytics__scroll">
      <ul class="home-analytics__list list-unstyled mb-0">
        <li
          v-for="item in reports"
          :key="item.key"
          class="home-analytics__item"
        >
          <Tooltip
            placement="top"
            :hide-delay="150"
            :text="reportActionTitle(item)"
          >
            <button
              type="button"
              class="home-analytics__link home-analytics__button"
              :disabled="Boolean(pendingReportType)"
              @click="openQuickReportModal(item)"
            >
              <span
                class="home-analytics__icon"
                :class="`home-analytics__icon--${item.tone}`"
                aria-hidden="true"
              >
                <i :class="item.icon" />
              </span>
              <span class="home-analytics__label">{{ t(`routes.analytics.${item.key}`) }}</span>
              <span
                v-if="pendingReportType === item.key"
                class="home-analytics__spinner spinner-border spinner-border-sm"
                aria-hidden="true"
              />
            </button>
          </Tooltip>
        </li>
      </ul>
    </div>

    <Teleport to="body">
      <div
        v-if="periodModalVisible"
        class="modal fade show d-block home-analytics-modal"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-analytics-modal-title"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5
                id="home-analytics-modal-title"
                class="modal-title"
              >
                {{ modalTitle }}
              </h5>
              <button
                type="button"
                class="btn-close"
                :aria-label="quickReportText('cancel')"
                :disabled="Boolean(pendingReportType)"
                @click="cancelQuickReport"
              />
            </div>
            <div
              v-if="selectedReportUsesYear || selectedReportUsesPeriod"
              class="modal-body"
            >
              <div
                v-if="selectedReportUsesYear"
                class="d-flex align-items-center gap-2"
              >
                <span>{{ quickReportText('year') }}</span>
                <SelectIdObject
                  v-model="quickSelectedYear"
                  :options="quickYearOptions"
                  :auto-sort="false"
                  class="home-analytics-modal__select"
                />
              </div>
              <div
                v-else-if="selectedReportUsesPeriod"
                class="row g-3"
              >
                <div class="col-md-6">
                  <label class="form-label">{{ quickReportText('from') }}</label>
                  <DatePicker
                    v-model="quickStartPeriod"
                    :allow-quarter-format="false"
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label">{{ quickReportText('to') }}</label>
                  <DatePicker
                    v-model="quickEndPeriod"
                    :allow-quarter-format="false"
                  />
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-outline-secondary"
                :disabled="Boolean(pendingReportType)"
                @click="cancelQuickReport"
              >
                {{ quickReportText('cancel') }}
              </button>
              <button
                type="button"
                class="btn btn-secondary"
                :disabled="Boolean(pendingReportType) || !canGenerateSelectedReport"
                @click="confirmQuickReport"
              >
                <span
                  v-if="Boolean(pendingReportType)"
                  class="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                />
                {{ quickReportText('generate') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="periodModalVisible"
        class="modal-backdrop fade show home-analytics-modal-backdrop"
      />
      <Docviewer
        v-if="docViewerVisible && docUpload"
        :upload="docUpload"
        :versioning="false"
        modal-size="xl"
        @hidden="onDocviewerHidden"
      />
    </Teleport>
  </section>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref } from 'vue'
import { format } from 'date-fns'
import { useI18n } from 'vue-i18n'
import { Modal as Docviewer } from 'soykajs/components/docviewer'
import DatePicker from '@/components/datepicker.vue'
import SelectIdObject from '@/components/select/selectIdObject.vue'
import Tooltip from '@/components/tooltip.vue'
import { useApi } from 'soykajs/composables/api'
import { useMessageStack } from '@/stores/messagestack'
import {
  ANALYTICS_REPORT_STATUS,
  HOME_ANALYTICS_REPORTS,
  getAnalyticsReportTypeMeta,
} from '@/lib/analyticsReports'

defineProps({
  /** Без заголовка — для модалки FAB Reports на стартовой. */
  hideTitle: { type: Boolean, default: false },
})

const emit = defineEmits(['report-viewer-open', 'report-viewer-close'])

const QUICK_REPORT_POLL_DELAY = 1500
const QUICK_REPORT_MAX_POLLS = 120
const QUICK_REPORT_FIRST_YEAR = 2024
const QUICK_REPORT_FUTURE_YEARS = 5

const { t, locale } = useI18n()
const { push } = useMessageStack()
const reports = HOME_ANALYTICS_REPORTS

const pendingReportType = ref('')
const docViewerVisible = ref(false)
const docUpload = ref(null)
const createdReportModel = ref(null)
const fetchedReportModel = ref(null)
const selectedReport = ref(null)
const periodModalVisible = ref(false)

const currentDate = new Date()
const currentYear = currentDate.getFullYear()
const quickStartPeriod = ref(new Date(currentYear, 0, 1))
const quickEndPeriod = ref(new Date(currentYear, 11, 31))
const quickSelectedYear = ref({ id: currentYear, name: String(currentYear) })

let pollTimeout = null
let resolvePoll = null

const { refresh: createHomeReport, abort: abortCreateHomeReport } = useApi({
  url: 'analytics/reports/home',
  model: createdReportModel,
  method: 'post',
})

const { refresh: fetchReport, abort: abortFetchReport } = useApi({
  url: 'analytics/reports/0',
  model: fetchedReportModel,
})

const quickReportLocale = computed(() => {
  const code = String(locale.value || '').toLowerCase()
  return code.startsWith('en') ? 'en' : 'ru'
})

// Подписи открытой модалки должны обновляться после смены языка.
const quickReportTexts = computed(() => ({
  title: t('quickReport.title'),
  year: t('quickReport.year'),
  from: t('quickReport.from'),
  to: t('quickReport.to'),
  generate: t('quickReport.generate'),
  cancel: t('quickReport.cancel'),
}))

const quickYearOptions = computed(() => {
  const items = []
  for (
    let year = currentYear + QUICK_REPORT_FUTURE_YEARS;
    year >= QUICK_REPORT_FIRST_YEAR;
    year--
  ) {
    items.push({ id: year, name: String(year) })
  }
  return items
})

const selectedReportMeta = computed(() => getAnalyticsReportTypeMeta(selectedReport.value?.key))
const selectedReportUsesYear = computed(
  () => selectedReportMeta.value?.periodMode === 'year' || Boolean(selectedReportMeta.value?.usesYear)
)
const selectedReportUsesPeriod = computed(
  () => selectedReportMeta.value?.periodMode === 'range' || Boolean(selectedReportMeta.value?.usesPeriod)
)
const modalTitle = computed(() => {
  if (!selectedReport.value?.key) {
    return quickReportText('title')
  }
  return `${quickReportText('title')}: ${t(`routes.analytics.${selectedReport.value.key}`)}`
})

function isValidDate(value) {
  return value instanceof Date && !Number.isNaN(value.getTime())
}

function isValidPeriodRange(start, end) {
  return isValidDate(start) && isValidDate(end) && start <= end
}

const canGenerateSelectedReport = computed(() => {
  if (!selectedReport.value?.key) {
    return false
  }
  if (selectedReportUsesYear.value) {
    return Boolean(quickSelectedYear.value?.id)
  }
  if (selectedReportUsesPeriod.value) {
    return isValidPeriodRange(quickStartPeriod.value, quickEndPeriod.value)
  }
  return true
})

function quickReportText(key) {
  return quickReportTexts.value[key] || ''
}

/**
 * Возвращает границы выбранного года в формате API.
 * @param {number|string} year Год отчёта.
 * @returns {{date_from: string, date_to: string}}
 */
function yearDateRange(year) {
  const safeYear = Number(year) || currentYear
  return {
    date_from: `${safeYear}-01-01`,
    date_to: `${safeYear}-12-31`,
  }
}

/**
 * Собирает минимальный payload для домашнего формирования аналитики.
 * @param {string} reportType Тип аналитического отчёта.
 * @returns {object} тело запроса quick-report API.
 */
function buildQuickReportPayload(reportType) {
  const payload = {
    report_type: reportType,
    options: {
      with_chart: true,
      locale: quickReportLocale.value,
    },
  }
  if (selectedReportUsesYear.value) {
    Object.assign(payload, yearDateRange(quickSelectedYear.value?.id))
  }
  if (selectedReportUsesPeriod.value) {
    payload.date_from = format(quickStartPeriod.value, 'yyyy-MM-dd')
    payload.date_to = format(quickEndPeriod.value, 'yyyy-MM-dd')
  }
  return payload
}

function reportActionTitle(item) {
  if (pendingReportType.value === item.key) {
    return t('quickReport.creating')
  }
  return t('quickReport.open', { name: t(`routes.analytics.${item.key}`) })
}

function clearPollTimeout() {
  if (pollTimeout !== null) {
    window.clearTimeout(pollTimeout)
    pollTimeout = null
  }
  if (resolvePoll) {
    resolvePoll(false)
    resolvePoll = null
  }
}

function waitBeforeNextPoll() {
  return new Promise(resolve => {
    resolvePoll = resolve
    pollTimeout = window.setTimeout(() => {
      pollTimeout = null
      resolvePoll = null
      resolve(true)
    }, QUICK_REPORT_POLL_DELAY)
  })
}

/**
 * Дожидается готового PDF для созданного отчёта.
 * @param {number} reportId ID созданного аналитического отчёта.
 * @returns {Promise<object|null>} готовый отчёт или null при размонтировании компонента.
 * @throws {Error} если формирование завершилось ошибкой или вышло за лимит ожидания.
 */
async function waitForGeneratedReport(reportId) {
  for (let attempt = 0; attempt < QUICK_REPORT_MAX_POLLS; attempt++) {
    await fetchReport({ url: `analytics/reports/home/${reportId}` })
    const report = fetchedReportModel.value
    if (
      Number(report?.status) === ANALYTICS_REPORT_STATUS.done &&
      report?.upload?.secure_filename
    ) {
      return report
    }
    if (Number(report?.status) === ANALYTICS_REPORT_STATUS.error) {
      throw new Error(t('quickReport.generationError'))
    }
    const shouldContinue = await waitBeforeNextPoll()
    if (!shouldContinue) {
      return null
    }
  }
  throw new Error(t('quickReport.timeout'))
}

function openReport(upload) {
  if (!upload?.secure_filename) {
    return
  }
  docUpload.value = {
    ...upload,
    filename: upload.filename || `${upload.secure_filename}.pdf`,
  }
  docViewerVisible.value = true
  emit('report-viewer-open')
}

function onDocviewerHidden() {
  docViewerVisible.value = false
  docUpload.value = null
  emit('report-viewer-close')
}

function openQuickReportModal(item) {
  if (pendingReportType.value) {
    return
  }
  selectedReport.value = item
  periodModalVisible.value = true
}

function cancelQuickReport() {
  if (pendingReportType.value) {
    return
  }
  periodModalVisible.value = false
  selectedReport.value = null
}

async function confirmQuickReport() {
  if (!canGenerateSelectedReport.value || !selectedReport.value) {
    return
  }
  await createQuickReport(selectedReport.value)
}

/**
 * Создаёт отчёт из стартовой панели и открывает готовый PDF в просмотрщике.
 * @param {object} item пункт домашней аналитики.
 */
async function createQuickReport(item) {
  if (pendingReportType.value) {
    return
  }
  pendingReportType.value = item.key
  try {
    await createHomeReport({ data: buildQuickReportPayload(item.key) })
    const createdReport = createdReportModel.value
    if (!createdReport?.id) {
      throw new Error(t('quickReport.createError'))
    }
    const report = createdReport?.upload?.secure_filename
      ? createdReport
      : await waitForGeneratedReport(createdReport.id)
    if (report?.upload?.secure_filename) {
      // Скрыть список отчётов под blur, закрыть период, затем PDF —
      // иначе body.docviewer-modal-open снимает blur с backdrop периода.
      emit('report-viewer-open')
      await nextTick()
      periodModalVisible.value = false
      selectedReport.value = null
      await nextTick()
      openReport(report.upload)
    }
  } catch (err) {
    push({ type: 'error', message: err?.message || t('quickReport.createError') })
  } finally {
    clearPollTimeout()
    pendingReportType.value = ''
  }
}

onUnmounted(() => {
  clearPollTimeout()
  abortCreateHomeReport()
  abortFetchReport()
})
</script>

<i18n>
{
  "ru": {
    "quickReport": {
      "title": "Период отчёта",
      "year": "Год",
      "from": "с",
      "to": "по",
      "generate": "Сформировать",
      "cancel": "Отмена",
      "open": "Сформировать: {name}",
      "creating": "Формируется отчёт",
      "createError": "Не удалось сформировать отчёт",
      "generationError": "Формирование отчёта завершилось ошибкой",
      "timeout": "Отчёт формируется дольше ожидаемого. Повторите формирование позже."
    }
  },
  "en": {
    "quickReport": {
      "title": "Report period",
      "year": "Year",
      "from": "From",
      "to": "To",
      "generate": "Generate",
      "cancel": "Cancel",
      "open": "Generate: {name}",
      "creating": "Generating report",
      "createError": "Could not generate report",
      "generationError": "Report generation failed",
      "timeout": "Report generation is taking longer than expected. Please try generating it again later."
    }
  }
}
</i18n>

<style scoped>
.home-analytics__button {
  border: 0;
  background: transparent;
  font: inherit;
  text-align: left;
}

.home-analytics__button:disabled {
  cursor: wait;
  opacity: 0.75;
}

.home-analytics__spinner {
  flex: 0 0 auto;
}

.home-analytics-modal__select {
  min-width: 10rem;
}

:global(.modal.home-analytics-modal) {
  z-index: 1210;
}

:global(.modal-backdrop.home-analytics-modal-backdrop) {
  z-index: 1200;
  background-color: rgba(7, 27, 58, 0.45);
  /* !important — иначе body.docviewer-modal-open снимает blur с .modal-backdrop */
  backdrop-filter: blur(12px) !important;
  -webkit-backdrop-filter: blur(12px) !important;
  opacity: 1;
  --bs-backdrop-opacity: 1;
}

:global(.home-analytics-modal .modal-dialog) {
  width: min(50vw, 760px);
  max-width: calc(100vw - 2rem);
}

@media (max-width: 767.98px) {
  :global(.home-analytics-modal .modal-dialog) {
    width: calc(100vw - 1rem);
  }
}
</style>
